'use client';

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as React from 'react';
import { Controller, type Control } from 'react-hook-form';

import { cn } from '@/utils';
import { Label, } from '../';

type RadixCheckboxProps = React.ComponentPropsWithoutRef<
  typeof CheckboxPrimitive.Root
>;

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export type BaseCheckboxProps = Omit<
  RadixCheckboxProps,
  'onCheckedChange' | 'onChange'
> & {
  label?: string | React.ReactNode;
  onChange?: RadixCheckboxProps['onCheckedChange'];
  error?: string;
  onClick?: () => void;
  helperText?: string;
  rootClassName?: string;
};

export type CheckboxProps = BaseCheckboxProps &
  (
    | {
        control: Control<any>;
        name: string;
      }
    | { control?: never }
  );
const CheckboxField = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  BaseCheckboxProps
>(
  (
    {
      className,
      onChange,
      onClick,
      rootClassName,
      label,
      helperText,
      error,
      ...props
    },
    ref
  ) => {
    const id = props.id || props.name;
    const hasError = Boolean(error);
    return (
      <div className={cn('flex items-center gap-2.5', rootClassName)}>
        <CheckboxPrimitive.Root
          ref={ref}
          id={id}
          className={cn(
            'peer size-4 bg-transparent rounded-md cursor-pointer shrink-0 border border-solid border-grayScale-400 dark:border-white hover:border-primary ring-gray-20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-blue-600 data-[state=checked]:text-white data-[state=checked]:bg-blue-600',
            className
          )}
          {...props}
          onCheckedChange={onChange}
        >
          <CheckboxPrimitive.Indicator
            className={cn('flex items-center justify-center text-current')}
          >
            <CheckIcon />
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
        {label && (
          <Label
            htmlFor={id}
            onClick={onClick}
            className={cn(hasError && 'text-red-500')}
          >
            {label}
          </Label>
        )}
        {helperText && (
          <span className="mt-[2px] text-gray-500">
            {helperText}
          </span>
        )}
        {error && (
          <span className="text-red-500 mt-[2px]">
            {error}
          </span>
        )}
      </div>
    );
  }
);
CheckboxField.displayName = CheckboxPrimitive.Root.displayName;

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(function CheckBox({ control, ...props }, ref) {
  return control ? (
    <Controller
      control={control}
      name={props.name as string}
      render={({
        field: { ref: _ref, value, ...field },
        fieldState: { error },
      }) => (
        <CheckboxField
          ref={ref}
          {...field}
          {...props}
          error={error?.message}
          checked={value}
        />
      )}
    />
  ) : (
    <CheckboxField ref={ref} {...props} />
  );
});

export { Checkbox };
